import {Component, OnDestroy, ViewChild} from '@angular/core';
import {IHeaderAngularComp} from 'ag-grid-angular';
import {ColDef, GridApi, IHeaderParams, RowNode} from 'ag-grid-community';
import {IconColourEnum} from '../../../shared-icons/models/icons-colour.enum';
import {
  DataAdditionalHeaderMenuOption,
} from '../../../../shared-utilities/models-old/ngp-report-grid/data-additional-header-menu-option';
import {Store} from '@ngrx/store';
import {IStore} from '../../../../shared/shared-models/store/store';
import {
  HeaderMenuColumnData,
  StoreHeaderMenuData,
} from '../../../../shared-utilities/models-old/ngp-report-grid/header-menu-data';
import {GridHeaderMenuActionTypes} from '../../../../shared/shared-models/grid-menu/header-menu-action-types';
import {
  selectFiltersAndToolsWithAppliedFilters,
  selectNGPReportsFiltered,
  selectNGPReportsMenuDataByStoreId,
} from '../../../../features/ngp-report/store/ngp-report.selectors';
import {NGPReport} from '../../../../shared-utilities/models-old/ngp-reports/ngp-report';
import {PRICE_BANDING_COLUMNS_FOR_NGP_REPORTS} from '../../../../shared/shared-utils/price/price-banding.utils';
import {
  selectEnablePriceBandingForStockManager,
  selectMultiStoreMenuData,
  selectStockManagerMenuDataByStoreId,
  selectTypesenseQueryData,
} from '../../../../features/stock-manager/store/stock-manager.selectors';
import {FiltersAndTools} from '../../../../shared-utilities/models-old/ngp-reports/filters-tools-ngp';
import {
  selectCurrentNavigationObj,
  selectSelectedStore,
} from '../../../../features/user-access/user-stores-store/user-store.selectors';
import {INavigate} from '../../../../features/user-access/models';
import {IconsEnum} from '../../../shared-icons/models/icons.enum';
import {selectAppliedAdvancedFilterGroup} from '../../../../features/core/store-shared-filter/shared-filter.selectors';
import {
  IAdvancedFilterGroup,
} from '../../../../features/settings-new/models/general/advanced-filter-group.interface';
import {Observable} from 'rxjs';
import {lockAllStockItems} from '../../../../features/stock-manager/store/stock-manager.actions';
import {TypesenseQueryData} from '../../../../shared/shared-models/type-sense/type-sense-types';
import {isNonEmptyObject} from '../../../../shared/shared-utils/object/object.utils';
import {TOOLTIP_TEXT} from '../../../../shared/shared-models/grid-menu/header-menu';
import {SharedPopoverComponent} from '../../../../shared/shared-module-ui/components/shared-popover/shared-popover.component';

@Component({
  selector: 'app-grid-column-header',
  templateUrl: './grid-column-header.component.html',
  styleUrls: ['./grid-column-header.component.scss'],
  standalone: false,
})
export class GridColumnHeaderComponent implements IHeaderAngularComp, OnDestroy {

  sortIcon = {};
  editingColumns = {};
  sortingColumnField: string | null = null;
  storeId: string | null = null;
  currentPage: string;
  indeterminate: boolean;
  params: IHeaderParams & {
    additionalData: DataAdditionalHeaderMenuOption<void>[];
  };
  selected: NGPReport[];
  ngpReport: NGPReport[];
  filtersAndTools: FiltersAndTools;
  isChecked: boolean = true;
  private isDragging: boolean = false;
  private dragTimeout: ReturnType<typeof setTimeout>;
  private sortChangedListener: () => void;
  protected readonly icons: typeof IconsEnum = IconsEnum;
  protected readonly iconColourEnum = IconColourEnum;
  protected tooltipText = TOOLTIP_TEXT();
  private priceBanding: boolean;
  private gridApi: GridApi;
  private editedAndSelectedItems: NGPReport[];
  appliedFilterGroup$: Observable<IAdvancedFilterGroup>;
  queryData$: Observable<TypesenseQueryData>;

  protected readonly isNonEmptyObject = isNonEmptyObject;

  @ViewChild('columnMenu') private columnMenu?: SharedPopoverComponent;

  constructor(
    private readonly store: Store,
  ) {
  }

  get getButtonUniqueID(): string {
    return !this.params?.column?.getColId() ? '' : `popover-button-${this.params?.column?.getColId()}`;
  }

  onHeaderMouseDown(): void {
    this.isDragging = false;
    if (this.dragTimeout) {
      clearTimeout(this.dragTimeout);
    }
    this.dragTimeout = setTimeout(() => {
      this.isDragging = true;
    }, 150);
  }

  onHeaderMouseUp(): void {
    if (this.dragTimeout) {
      clearTimeout(this.dragTimeout);
      this.dragTimeout = null;
    }
    if (this.isDragging) {
      setTimeout(() => {
        this.isDragging = false;
      }, 100);
    }
  }

  onButtonClick(event: Event): void {
    if (this.isDragging) {
      event.preventDefault();
      event.stopPropagation();
      event.stopImmediatePropagation();
      return;
    }
    if (this.columnMenu?.isRendered) {
      this.columnMenu.dismiss();
    } else {
      this.columnMenu?.present(event);
    }
  }

  onMenuItemClick(event: Event, menuItem: DataAdditionalHeaderMenuOption<void>): void {
    if (menuItem.options?.length > 0) {
      return;
    }
    menuItem.callback(menuItem.callbackParams);
  }

  onSubmenuItemClick(opt: DataAdditionalHeaderMenuOption<void>): void {
    opt.callback(opt.callbackParams);
    SharedPopoverComponent.dismissAll();
  }

  getDisplayedRows(): NGPReport[] {
    if (this.params.api.isDestroyed() || this.params.api == null) {
      return;
    }
    const gridApi: GridApi = this.params.api;
    const displayedRows: NGPReport[] = [];
    const pageSize = gridApi.paginationGetPageSize();
    const pageNumber = gridApi.paginationGetCurrentPage() + 1;
    const startingPoint = (pageSize * pageNumber) - pageSize;
    for (let i = startingPoint; i < pageSize * pageNumber; i++) {
      const node = gridApi.getDisplayedRowAtIndex(i);
      if (node?.data) {
        displayedRows.push(node.data as NGPReport);
      }
    }
    return displayedRows;
  }

  agInit(params: IHeaderParams & {dataAdditional: DataAdditionalHeaderMenuOption<void>[]}): void {
    if (params.context?.tooltipText) {
      this.tooltipText = params.context.tooltipText;
    }
    this.assignComponentProperties(params);
    this.store.select(selectCurrentNavigationObj).subscribe((currentPageObj: {
      currentSelectedPage: INavigate
    }): void => {
      this.currentPage = currentPageObj.currentSelectedPage.currentPage;
    });
    this.store.select(selectSelectedStore).subscribe(((store: IStore): void => {
      this.storeId = store.storeId;
    }));

    if (this.currentPage === 'ngp-report') {
      this.store.select(selectNGPReportsMenuDataByStoreId).subscribe((menuData: StoreHeaderMenuData): void => {
        this.handleHeaderMenuData(menuData, params);
      });
      this.store.select(selectNGPReportsFiltered).subscribe((ngpReport: NGPReport[]): void => {
        this.ngpReport = [...ngpReport];
        this.selected = [...ngpReport.filter((report: NGPReport) => report.isSelected)];
        this.editedAndSelectedItems = [...ngpReport.filter((report: NGPReport) => report.isSelected || report.isEdited)];
      });
      this.store.select(selectFiltersAndToolsWithAppliedFilters).subscribe((filtersAndTools) => {
        this.filtersAndTools = filtersAndTools;
      });
    } else if (this.currentPage === 'stock-manager') {
      const menuData$ = params.context?.isMultiStoreMode
        ? this.store.select(selectMultiStoreMenuData)
        : this.store.select(selectStockManagerMenuDataByStoreId);
      menuData$.subscribe((menuData: StoreHeaderMenuData): void => {
        this.handleHeaderMenuData(menuData, params);
      });
      this.store.select(selectEnablePriceBandingForStockManager).subscribe((isPriceBanding: boolean): void => {
        this.priceBanding = isPriceBanding;
      });
      this.queryData$ = this.store.select(selectTypesenseQueryData);
    }

    this.appliedFilterGroup$ = this.store.select(selectAppliedAdvancedFilterGroup);
    this.gridApi = params.api;
  }

  refresh(params: IHeaderParams): boolean {
    if (params.context?.tooltipText) {
      this.tooltipText = params.context.tooltipText;
    }
    return true;
  }

  handleHeaderMenuData(
    menuData: StoreHeaderMenuData,
    params: IHeaderParams &
      {dataAdditional: DataAdditionalHeaderMenuOption<void>[]}
  ): void {
    if (menuData && Object.keys(menuData).length !== 0) {
      if (!this.sortIcon) {
        this.sortIcon = {};
      }

      if (!this.sortIcon[this.storeId]) {
        this.sortIcon[this.storeId] = {};
      }
      this.sortIcon[this.storeId][params.column.getColDef().field] = null;
      this.editingColumns = {};
      this.setHeaderIcons(menuData);
    } else {
      const key: string = params.column.getColDef().field;
      const insideOwnColumnHeader = this.params.column.getColDef().field === key;
      this.setSortButtonsColour(insideOwnColumnHeader, GridHeaderMenuActionTypes.sortUnSort);
      if (insideOwnColumnHeader) {
        this.changeAdditionalDataParameter(GridHeaderMenuActionTypes.editStart, true, 'visible');
        this.changeAdditionalDataParameter(GridHeaderMenuActionTypes.editStop, false, 'visible');
        this.changeAdditionalDataParameter(GridHeaderMenuActionTypes.priceBandingStart, true, 'visible');
        this.changeAdditionalDataParameter(GridHeaderMenuActionTypes.priceBandingStop, false, 'visible');
      }
      if (!this.sortIcon) {
        this.sortIcon = {};
      }
      if (!this.sortIcon[this.storeId]) {
        this.sortIcon[this.storeId] = {};
      }
      if (!this.editingColumns) {
        this.editingColumns = {};
      }
      if (!this.editingColumns[this.storeId]) {
        this.editingColumns[this.storeId] = {};
      }
      this.sortIcon[this.storeId][key] = null;
      this.editingColumns[this.storeId][key] = false;
    }
  }

  isButtonVisible(menuItem: DataAdditionalHeaderMenuOption<void>): boolean {
    const colDef: ColDef = this.params.column.getColDef();
    const priceBandingFields: string[] = PRICE_BANDING_COLUMNS_FOR_NGP_REPORTS;
    if (colDef?.lockVisible && menuItem.icon === IconsEnum.eyeOffOutline) {
      return false;
    }
    if (colDef?.cellEditor === false && menuItem.icon === IconsEnum.pencil) {
      return false;
    }

    if (menuItem.icon === IconsEnum.sort && this.isFieldUnsortable(colDef?.field)) {
      return false;
    }
    if (colDef?.field === '_tags' && menuItem.icon === IconsEnum.sort) {
      return false;
    }
    if (!(priceBandingFields.includes(colDef?.field)) && menuItem.icon === IconsEnum.bandage) {
      return false;
    }
    return menuItem.visible || false;
  }

  private getCurrentPageLeafNodes(): RowNode[] {
    if (!this.gridApi) return [];
    const page = this.gridApi.paginationGetCurrentPage?.() ?? 0;
    const pageSize = this.gridApi.paginationGetPageSize?.() ?? this.gridApi.getDisplayedRowCount();
    const start = page * pageSize;
    const end = Math.min(start + pageSize, this.gridApi.getDisplayedRowCount());
    const nodes: RowNode[] = [];
    for (let i = start; i < end; i++) {
      const n = this.gridApi.getDisplayedRowAtIndex(i);
      if (n && !n.group) nodes.push(n as RowNode);
    }
    return nodes;
  }

  getIndeterminateForPage(): boolean {
    const nodes = this.getCurrentPageLeafNodes();
    if (!nodes.length) return false;
    const sel = nodes.filter(n => n.isSelected()).length;
    return sel > 0 && sel < nodes.length;
  }

  getSelectedForPage(): boolean {
    const nodes = this.getCurrentPageLeafNodes();
    if (!nodes.length) return false;
    return nodes.every(n => n.isSelected());
  }

  toggleSelection(): void {
    const nodes = this.getCurrentPageLeafNodes();
    if (!nodes.length) return;
    const selectAll = !this.getSelectedForPage();
    nodes.forEach(n => {
      n.setSelected(selectAll, false);
    });
  }

  onSimpleHeaderClick(event: Event): void {
    if (this.isDragging) {
      event.preventDefault();
      event.stopPropagation();
      event.stopImmediatePropagation();
      return;
    }
    if (this.params.enableSorting) {
      this.params.progressSort((event as MouseEvent).shiftKey);
    }
  }

  private isFieldUnsortable(field?: string): boolean {
    const unsortableFields = ['multipleSuppliers', 'orderQty', 'modified', 'depName', 'subDepName'];

    if (!field) return false;

    if (unsortableFields.includes(field)) return true;

    if (field === 'nominalGP' && this.currentPage === 'stock-manager') return true;

    return false;
  }

  onLockAllStockItemsClick(enable: boolean): void {
    this.store.dispatch(lockAllStockItems({enable}));
  }

  ngOnDestroy(): void {
    if (this.dragTimeout) {
      clearTimeout(this.dragTimeout);
    }
  }

  private setHeaderIcons(menuData: StoreHeaderMenuData | Record<string, HeaderMenuColumnData>): void {
    Object.keys(menuData).forEach((key: string) => {
      const insideOwnColumnHeader = this.params.column.getColDef().field === key;
      switch (menuData[key]?.sortType) {
        case 'sort-asc':
          this.setSortButtonsColour(insideOwnColumnHeader, GridHeaderMenuActionTypes.sortAsc);
          this.sortingColumnField = key;
          if (!this.sortIcon[this.storeId]) {
            this.sortIcon[this.storeId] = {};
          }
          this.sortIcon[this.storeId][key] = this.icons.arrowDown;
          break;
        case 'sort-desc':
          this.setSortButtonsColour(insideOwnColumnHeader, GridHeaderMenuActionTypes.sortDesc);
          this.sortingColumnField = key;
          if (!this.sortIcon[this.storeId]) {
            this.sortIcon[this.storeId] = {};
          }
          this.sortIcon[this.storeId][key] = this.icons.arrowUp;
          break;
        case 'sort-unsort':
          this.setSortButtonsColour(insideOwnColumnHeader, GridHeaderMenuActionTypes.sortUnSort);
          if (!this.sortIcon[this.storeId]) {
            this.sortIcon[this.storeId] = {};
          }
          this.sortIcon[this.storeId][key] = null;
          break;
        default:
          break;
      }
      switch (menuData[key]?.isEditing) {
        case true:
          if (!this.editingColumns[this.storeId]) {
            this.editingColumns[this.storeId] = {};
          }
          this.editingColumns[this.storeId][key] = true;
          if (insideOwnColumnHeader) {
            if (this.params?.api?.getColumnDef(key)?.editable === false) {
              setTimeout(() => {
                const api = this.params.api;
                const colDefs: ColDef[] = api.getColumnDefs();
                const colIndex = colDefs.findIndex((colDef: ColDef) => colDef.field === key);
                if (colIndex !== -1) {
                  const currentColDef = colDefs[colIndex];
                  currentColDef.editable = true;
                  const currentTypes = Array.isArray(currentColDef.type) ? currentColDef.type : [];
                  currentColDef.type = [...currentTypes, 'cellEdit'];
                  api.setGridOption('columnDefs', [...colDefs]);
                }
              }, 0);
            }

            this.changeAdditionalDataParameter(GridHeaderMenuActionTypes.editStart, false, 'visible');
            this.changeAdditionalDataParameter(GridHeaderMenuActionTypes.editStop, true, 'visible');
          }
          break;
        case false:
          if (insideOwnColumnHeader) {
            this.changeAdditionalDataParameter(GridHeaderMenuActionTypes.editStart, true, 'visible');
            this.changeAdditionalDataParameter(GridHeaderMenuActionTypes.editStop, false, 'visible');
          }
          if (!this.editingColumns[this.storeId]) {
            this.editingColumns[this.storeId] = {};
          }
          this.editingColumns[this.storeId][key] = false;
          break;
        default:
          break;
      }

      switch (menuData[key]?.isPriceBanding) {
        case true:
          if (insideOwnColumnHeader) {
            this.changeAdditionalDataParameter(GridHeaderMenuActionTypes.priceBandingStart, false, 'visible');
            this.changeAdditionalDataParameter(GridHeaderMenuActionTypes.priceBandingStop, true, 'visible');
          }
          break;
        case false:
          if (insideOwnColumnHeader) {
            this.changeAdditionalDataParameter(GridHeaderMenuActionTypes.priceBandingStart, true, 'visible');
            this.changeAdditionalDataParameter(GridHeaderMenuActionTypes.priceBandingStop, false, 'visible');
          }
          break;
        default:
          break;
      }
    });
  }

  private setSortButtonsColour(insideOwnColumnHeader: boolean, sortType: GridHeaderMenuActionTypes): void {
    if (sortType !== GridHeaderMenuActionTypes.sortUnSort) {
      this.changeAdditionalDataParameter(GridHeaderMenuActionTypes.sortButton, 'white', 'colour');
      this.changeAdditionalDataParameter(GridHeaderMenuActionTypes.sortButton, 'white', 'colour', true, GridHeaderMenuActionTypes.sortAsc);
      this.changeAdditionalDataParameter(GridHeaderMenuActionTypes.sortButton, 'white', 'colour', true, GridHeaderMenuActionTypes.sortDesc);
      this.changeAdditionalDataParameter(GridHeaderMenuActionTypes.sortButton, 'white', 'colour', true, GridHeaderMenuActionTypes.sortUnSort);
      if (insideOwnColumnHeader) {
        this.changeAdditionalDataParameter(GridHeaderMenuActionTypes.sortButton, 'yellow', 'colour');
        this.changeAdditionalDataParameter(GridHeaderMenuActionTypes.sortButton, 'yellow', 'colour', true, sortType);
      }
    } else {
      this.changeAdditionalDataParameter(GridHeaderMenuActionTypes.sortButton, 'white', 'colour');
      this.changeAdditionalDataParameter(GridHeaderMenuActionTypes.sortButton, 'white', 'colour', true, GridHeaderMenuActionTypes.sortAsc);
      this.changeAdditionalDataParameter(GridHeaderMenuActionTypes.sortButton, 'white', 'colour', true, GridHeaderMenuActionTypes.sortDesc);
      this.changeAdditionalDataParameter(GridHeaderMenuActionTypes.sortButton, 'yellow', 'colour', true, GridHeaderMenuActionTypes.sortUnSort);
    }
  }

  private changeAdditionalDataParameter(
    actionToChange: GridHeaderMenuActionTypes,
    propertyNewValue: string | number | boolean | null,
    propertyToChange: string,
    isOptionChanging?: boolean,
    optionAction?: GridHeaderMenuActionTypes
  ): void {
    if (isOptionChanging === undefined) {
      isOptionChanging = false;
    }
    const menuItem: DataAdditionalHeaderMenuOption<void> | undefined = this.params.additionalData
      ?.find((ad: DataAdditionalHeaderMenuOption<void>): boolean => ad.callbackParams.action === actionToChange);
    if (!menuItem) {
      return;
    }
    if (isOptionChanging) {
      const option: DataAdditionalHeaderMenuOption<void> | undefined = menuItem.options
        ?.find((opt: DataAdditionalHeaderMenuOption<void>): boolean => opt.callbackParams.action === optionAction);
      if (!option) {
        return;
      }
      option[propertyToChange] = propertyNewValue;
    } else {
      menuItem[propertyToChange] = propertyNewValue;
    }
  }

  private assignComponentProperties(
    params: IHeaderParams & {dataAdditional: DataAdditionalHeaderMenuOption<void>[]}
  ): void {
    const column = params.column;
    const headerOptions = column.getColDef().headerComponentParams;

    const options = [];

    headerOptions.forEach((option: DataAdditionalHeaderMenuOption<void>): void => {
      const optionOptions = option?.options?.length > 0 ? option.options : [];
      let optCopy = [];

      const newOption: DataAdditionalHeaderMenuOption<void> = {
        ...option,
        callbackParams: {
          ...option.callbackParams,
          colId: params.column.getColId()
        }
      };

      if (optionOptions.length > 0) {
        optCopy = [];
        optionOptions.forEach((oo: DataAdditionalHeaderMenuOption<void>) => {
          optCopy.push({
            ...oo,
            callbackParams: {
              ...oo.callbackParams,
              colId: params.column.getColId()
            }
          });
        });
        newOption.options = optCopy;
      }
      options.push(newOption);
    });
    this.params = {
      ...params,
      additionalData: [...options]
    };
  }

}
